import { EditOutlined, EyeOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, Card, Row, Tag, theme, Typography } from 'antd';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src/';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { TutorialVideoPlayer } from 'src/common/components/video-player/video-player';
import { emptyTableModel, LANGUAGE_NAMES } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import {
  getLocalizedDataFromTutorialItem,
  getTutorialDurationLabel,
  TutorialItemStore,
  TutorialSummary,
  TutorialSummaryStore,
} from 'src/stores/video-tutorials-store';
import { TutorialDetails } from './tutorial-details';
import { Languages } from 'src/i18n';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';

const { Text } = Typography;
const { useToken } = theme;

const TutorialsListPage: FC = () => {
  const { t, i18n } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);

  const [query, setQuery] = useState({
    searchQuery: '',
    skip: 0,
    take: 10,
  } as Query);

  const tutorialsStore = container.get(TutorialSummaryStore);
  const tutorialItemStore = container.get(TutorialItemStore);
  const tutorialsState = tutorialsStore.state;
  const [tutorialDetails, setTutorialDetails] = useState<TutorialSummary | undefined>();
  const [tutorialViewDetails, setTutorialViewDetails] = useState<TutorialSummary | undefined>();
  const [synchronizingVideos, setSynchronizingVideos] = useState(false);

  const selectedRows = useRef<Array<string>>([]);
  const { token } = useToken();

  const titleFilterField = i18n.language === Languages.English ? 'title' : `title${LANGUAGE_NAMES[i18n.language]}`;
  const [newVideoVisible, setNewVideoVisible] = useState(false);

  setHeader(t('Tutorials'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'tutorials', breadcrumbName: t('VideoTutorials') },
  ]);

  useEffect(
    () => {
      tutorialsStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query: query,
    columns: [
      {
        sortable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={tutorialsStore.baseUrl} fieldName={titleFilterField} fieldValue={titleFilterField} />,
        fiterField: titleFilterField,
        field: 'title',
        title: t('Title'),
        renderer: (data) => <span>{getLocalizedDataFromTutorialItem(data, i18n.language as Languages).title}</span>,
      },
      {
        sortable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={tutorialsStore.baseUrl} fieldName={'functionTitle'} fieldValue={'functionTitle'} />,
        fiterField: 'functionTitle',
        field: 'functionTitle',
        title: t('FunctionTitle'),
        renderer: (data) => (data.functionTitle ? <Tag>{data.functionTitle}</Tag> : ''),
      },
      {
        sortable: false,
        fiterField: 'functionOrder',
        field: 'functionOrder',
        title: t('Order'),
        renderer: (data) => <span>{data.functionOrder}</span>,
      },
      {
        sortable: true,
        field: 'durationInSeconds',
        title: t('Duration'),
        renderer: (data) => <span>{`${getTutorialDurationLabel(data)} ${t('MinutesAbbreviation')}`}</span>,
      },
      {
        sortable: false,
        field: 'permissions',
        title: t('Permissions'),
        renderer: (data) => (
          <>
            {data.permissions.map((v) => (
              <Tag key={v.id} style={{ margin: 4 }}>
                {t(v.permissionName)}
              </Tag>
            ))}
          </>
        ),
      },
      {
        sortable: true,
        searchable: false,
        field: 'isEnabled',
        title: t('Enabled'),
        filter: (
          <OptionsFilter
            options={[
              { label: t('Yes'), value: true },
              { label: t('No'), value: false },
            ]}
          />
        ),
        renderer: (data) =>
          data?.isEnabled ? (
            <span style={{ color: token.colorSuccess }}>{t('Yes')}</span>
          ) : (
            <span style={{ color: token.colorError }}>{t('No')}</span>
          ),
      },
      {
        sortable: false,
        field: 'link',
        title: t('Link'),
        renderer: (data) =>
          data.link ? (
            <Text copyable={{ text: data.link }}>
              <a href={data.link} target="_blank" rel="noopener noreferrer">
                {data.link}
              </a>
            </Text>
          ) : undefined,
      },
      {
        fixed: 'right',
        sortable: false,
        searchable: false,
        field: 'edit',
        title: t('Edit'),
        renderer: (data) => {
          let itemData = data as TutorialSummary;
          return (
            <span
              className="clickable-span strong"
              onClick={() => {
                setTutorialDetails(() => itemData);
              }}
            >
              {<EditOutlined />}
            </span>
          );
        },
      },
      {
        sortable: false,
        searchable: false,
        field: 'edit',
        title: t('View'),
        renderer: (data) => {
          let itemData = data as TutorialSummary;
          return (
            <span
              className="clickable-span strong"
              onClick={() => {
                setTutorialViewDetails(() => itemData);
              }}
            >
              {<EyeOutlined />}
            </span>
          );
        },
      },
      {
        fixed: 'right',
        sortable: false,
        searchable: false,
        field: 'synchronize',
        title: t('Synchronize'),
        renderer: (data) => {
          let itemData = data as TutorialSummary;
          return (
            <span
              className="clickable-span strong"
              onClick={async () => {
                await tutorialItemStore.synchronizeVideo(itemData.id);
                tutorialsStore.load(query);
              }}
            >
              {<SyncOutlined />}
            </span>
          );
        },
      },
    ],
    data: tutorialsState.value,
  } as TableModel<TutorialSummary>;

  return (
    <>
      <Row align="top" justify="space-between" className="table-overflow">
        <Card className="card-table" style={{ width: '100%' }}>
          <TableView
            rightToolbar={
              <>
                <li className="table-buttons-list-element">
                  <Button icon={<PlusOutlined />} loading={synchronizingVideos} type="primary" onClick={() => setNewVideoVisible(true)}>
                    {t('Add')}
                  </Button>
                </li>
                <li className="table-buttons-list-element">
                  <Button
                    loading={synchronizingVideos}
                    type="primary"
                    onClick={async () => {
                      setSynchronizingVideos(true);
                      await tutorialsStore.synchronizeVideos();
                      setSynchronizingVideos(false);
                      tutorialsStore.load(query);
                    }}
                  >
                    {t('SynchronizeVideos')}
                  </Button>
                </li>
              </>
            }
            onSelectionChanged={(rows: Array<string>) => {
              selectedRows.current = rows;
            }}
            onDeleteRow={async () => {
              if (selectedRows.current.length === 0) return;
              const idToDelete = [...selectedRows.current];
              selectedRows.current = [];
              await tutorialsStore.deleteItems(idToDelete);
            }}
            canDelete={true}
            canSort
            onQueryChanged={(query: Query) => {
              setQuery(query);
              tutorialsStore.load(query);
            }}
            onRefresh={() => tutorialsStore.load(query)}
            model={tutorialsState.errorMessage.value ? emptyTableModel : tableModel}
            error={
              tutorialsState.errorMessage.value &&
              (config.environment === ENV.DEV ? formatMessage(tutorialsState.errorMessage.value) : t('TutorialsTableError'))
            }
          />
        </Card>
      </Row>

      {tutorialDetails !== undefined && (
        <TutorialDetails
          mode="edit"
          item={tutorialDetails}
          onClose={(response) => {
            setTutorialDetails(undefined);
            if (response?.isSuccess) tutorialsStore.load(query);
          }}
        />
      )}

      {newVideoVisible && (
        <TutorialDetails
          mode="new"
          item={undefined}
          onClose={(response) => {
            setNewVideoVisible(false);
            if (response?.isSuccess) tutorialsStore.load(query);
          }}
        />
      )}

      {tutorialViewDetails && (
        <TutorialVideoPlayer
          videoData={tutorialViewDetails}
          hideVideoPlayer={() => {
            setTutorialViewDetails(undefined);
          }}
        />
      )}
    </>
  );
};

export default TutorialsListPage;
